/**
 * i18n mínima y tipada.
 *
 * Sin librería: tres idiomas, un catálogo plano y una sustitución de
 * `{placeholders}`. Traer i18next para esto sería 40 kB en un móvil que
 * descarga por una línea doméstica, y no resolvería el único problema que
 * importa aquí, que es **no dejarse ningún texto sin traducir**. Eso lo
 * resuelve el tipo: `en.ts` y `de.ts` están declarados como
 * `Record<keyof typeof es, string>`, así que una clave que falte no compila.
 *
 * El idioma se guarda por empleada y viene del servidor (`employee.idioma`).
 * El del navegador solo se usa antes de tener sesión.
 *
 * ### El alemán entró después, y eso deja una huella que hay que conocer
 *
 * `es` y `en` estuvieron solos desde el primer commit, así que por el código
 * había repartidos ternarios de la forma `idioma === 'en' ? x.nombre_en :
 * x.nombre_es`. Un ternario binario sobre el idioma **no falla** cuando llega
 * un tercero: elige el español y se queda tan ancho. O sea que el panel salía
 * en alemán con los nombres de las estancias en español, sin un error de
 * compilación, sin un aviso en consola y sin que ninguna prueba lo viera. Por
 * eso el campo traducido de un dato se lee SIEMPRE con `enIdioma()`, que tiene
 * la cadena de respaldo en un solo sitio y se puede medir.
 */

import { de } from './de.js';
import { en } from './en.js';
import { es } from './es.js';
import { huespedDe } from './huesped-de.js';
import { huespedEn } from './huesped-en.js';
import { huespedEs } from './huesped-es.js';
import { manualDe } from './manual-de.js';
import { manualEn } from './manual-en.js';
import { manualEs } from './manual-es.js';
import { oficinaDe } from './oficina-de.js';
import { oficinaEn } from './oficina-en.js';
import { oficinaEs } from './oficina-es.js';

export type Idioma = 'es' | 'en' | 'de';
export type Clave = keyof typeof es;
export type Valores = Readonly<Record<string, string | number>>;

export const IDIOMAS: readonly Idioma[] = ['es', 'en', 'de'] as const;
export const IDIOMA_POR_DEFECTO: Idioma = 'es';

/**
 * El nombre de cada idioma EN SU PROPIO idioma.
 *
 * «Deutsch» y no «Alemán». Quien busca su idioma en una lista lo reconoce
 * escrito como lo escribe él, y el momento en que alguien abre un selector de
 * idioma es justo el momento en que la pantalla puede estar en uno que no
 * entiende: un nombre traducido a ese otro idioma no le sirve de nada.
 *
 * Por eso tampoco lleva clave de i18n. No depende del idioma de la interfaz,
 * así que meterlo en los catálogos sería tres copias de lo mismo esperando a
 * descuadrarse.
 *
 * Esto estaba escrito como `i === 'es' ? 'Español' : 'English'` en DOS sitios
 * —la pantalla de alta de la empleada y la ficha del panel—, los dos sobre una
 * lista `['es', 'en'] as const` también escrita a mano. Ninguno de los dos
 * falla al añadir un idioma: siguen ofreciendo dos y no lo dicen.
 */
export const NOMBRE_IDIOMA: Record<Idioma, string> = {
    es: 'Español',
    en: 'English',
    de: 'Deutsch',
};

const CATALOGOS: Record<Idioma, Record<Clave, string>> = { es, en, de };

/**
 * Traduce y sustituye.
 *
 * Un `{placeholder}` sin valor se deja tal cual, a la vista. Es a propósito:
 * un hueco vacío pasa desapercibido en una revisión y "Te quedan {intentos}
 * intentos" salta a la primera.
 */
export function traducir(idioma: Idioma, clave: Clave, valores?: Valores): string {
    return sustituir(CATALOGOS[idioma][clave] ?? CATALOGOS[IDIOMA_POR_DEFECTO][clave], valores);
}

/**
 * Sustituye `{nombre}` y elige entre `{nombre:singular|plural}`.
 *
 * Los plurales existen porque el catálogo entero los escribía a pelo y con
 * uno decía **«Faltan 1 fotos»**, «1 estancias», «te quedan 1 intentos». Lo
 * ve una empleada todos los días, y el de los intentos sale justo en el peor
 * momento: el quinto PIN mal bloquea el móvil, así que el aviso del cuarto es
 * exactamente cuando dice «te quedan 1 intentos».
 *
 * La forma es explícita —`{n:estancia|estancias}`— y no una regla mágica que
 * mire «el último número sustituido»: en `{n}/{total}` hay dos, y adivinar
 * cuál manda es como acabas con un texto que cambia de forma sin motivo.
 * Además así el catálogo se lee: quien traduce ve las dos formas juntas.
 *
 * Solo singular y plural. El español, el inglés y el alemán tienen dos formas
 * cada uno, así que una tabla de reglas CLDR sería resolver un problema que no
 * existe. El alemán concuerda además el verbo, igual que el español —«1 Foto
 * fehlt» / «2 Fotos fehlen»—, y para eso ya vale poner dos `{n:…|…}` en la
 * misma frase, que es lo que ya hace el español.
 */
function sustituir(texto: string, valores?: Valores): string {
    if (valores === undefined) {
        return texto;
    }

    return texto.replace(
        /\{(\w+)(?::([^{}|]*)\|([^{}]*))?\}/g,
        (todo, nombre: string, uno?: string, varios?: string) => {
            const v = valores[nombre];

            // Un `{placeholder}` sin valor se deja tal cual, a la vista: un
            // hueco vacío pasa desapercibido en una revisión y «Te quedan
            // {intentos} intentos» salta a la primera.
            if (v === undefined) {
                return todo;
            }

            if (uno === undefined || varios === undefined) {
                return String(v);
            }

            return Number(v) === 1 ? uno : varios;
        }
    );
}

export type T = (clave: Clave, valores?: Valores) => string;

// --- El catálogo del panel, aparte -----------------------------------------
//
// Dos catálogos y no uno: la empleada descarga su aplicación por la línea de
// la oficina en un móvil, y no tiene por qué llevarse encima los textos de un
// panel de escritorio que nunca va a ver.

export type ClaveOficina = keyof typeof oficinaEs;
export type TOficina = (clave: ClaveOficina, valores?: Valores) => string;

const CATALOGOS_OFICINA: Record<Idioma, Record<ClaveOficina, string>> = {
    es: oficinaEs,
    en: oficinaEn,
    de: oficinaDe,
};

export function traducirOficina(idioma: Idioma, clave: ClaveOficina, valores?: Valores): string {
    return sustituir(
        CATALOGOS_OFICINA[idioma][clave] ?? CATALOGOS_OFICINA[IDIOMA_POR_DEFECTO][clave],
        valores
    );
}

export function crearTOficina(idioma: Idioma): TOficina {
    return (clave, valores) => traducirOficina(idioma, clave, valores);
}

/** El `t` que usan los componentes, ya atado a un idioma. */
export function crearT(idioma: Idioma): T {
    return (clave, valores) => traducir(idioma, clave, valores);
}

export function esIdioma(v: unknown): v is Idioma {
    return IDIOMAS.includes(v as Idioma);
}

/** Solo para la pantalla de alta, antes de saber quién es la empleada. */
export function idiomaDelNavegador(): Idioma {
    if (typeof navigator === 'undefined') {
        return IDIOMA_POR_DEFECTO;
    }

    for (const lang of navigator.languages ?? [navigator.language]) {
        const corto = lang.slice(0, 2).toLowerCase();
        if (esIdioma(corto)) {
            return corto;
        }
    }

    return IDIOMA_POR_DEFECTO;
}

// --- Y el del huésped, que son quince textos --------------------------------

export type ClaveHuesped = keyof typeof huespedEs;
export type THuesped = (clave: ClaveHuesped, valores?: Valores) => string;

const CATALOGOS_HUESPED: Record<Idioma, Record<ClaveHuesped, string>> = {
    es: huespedEs,
    en: huespedEn,
    de: huespedDe,
};

export function crearTHuesped(idioma: Idioma): THuesped {
    return (clave, valores) =>
        sustituir(
            CATALOGOS_HUESPED[idioma][clave] ?? CATALOGOS_HUESPED[IDIOMA_POR_DEFECTO][clave],
            valores
        );
}

// --- Y el manual, que es el cuarto y lo comparten las dos apps -------------
//
// Catalogo propio por lo mismo que el del huesped: son textos largos que se
// leen una vez. Lo comparten el panel y el movil --el manual del panel enseña
// TAMBIEN la mitad del movil, porque quien esta en la oficina atiende el
// telefono de quien esta en un piso-- asi que separarlos seria mantener dos
// veces lo mismo para que acaben diciendo cosas distintas.

export type ClaveManual = keyof typeof manualEs;
export type TManual = (clave: ClaveManual, valores?: Valores) => string;

const CATALOGOS_MANUAL: Record<Idioma, Record<ClaveManual, string>> = {
    es: manualEs,
    en: manualEn,
    de: manualDe,
};

export function crearTManual(idioma: Idioma): TManual {
    return (clave, valores) =>
        sustituir(
            CATALOGOS_MANUAL[idioma][clave] ?? CATALOGOS_MANUAL[IDIOMA_POR_DEFECTO][clave],
            valores
        );
}

export {
    es,
    en,
    de,
    oficinaEs,
    oficinaEn,
    oficinaDe,
    huespedEs,
    huespedEn,
    huespedDe,
    manualEs,
    manualEn,
    manualDe,
};
